/**
 * The two input-less bulk dialogs of the PO Lines tab: the Accept /
 * Acknowledge confirmation and the failed-lines log.
 *
 * Moved out of POLinesTab.tsx unchanged (markup and copy as they were) so the
 * tab file is back under the 500-line limit — Customer review 11 Sep (stream I:
 * the deep-link modal fix touched that file).
 */
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../ui/dialog';
import { Button } from '../../../ui/button';

interface BulkAcceptConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** How many lines the confirmation is for. */
  count: number;
  /** CR-5972: Accept for Changed lines, Acknowledge for New lines — copy only; same API call. */
  verb: 'accept' | 'acknowledge';
  accepting: boolean;
  onConfirm: () => void;
}

export function BulkAcceptConfirmDialog({ open, onOpenChange, count, verb, accepting, onConfirm }: BulkAcceptConfirmDialogProps) {
  const verbCap = verb === 'acknowledge' ? 'Acknowledge' : 'Accept';
  const verbIng = verb === 'acknowledge' ? 'Acknowledging' : 'Accepting';
  const verbInf = verb === 'acknowledge' ? 'acknowledge' : 'accept';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{verbCap} Selected Lines</DialogTitle>
          <DialogDescription className="sr-only">
            Confirm bulk {verbInf} of selected PO lines
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to {verbInf} {count} selected line{count > 1 ? 's' : ''}?
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
              onConfirm();
            }}
            disabled={accepting}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {accepting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {verbIng}...
              </>
            ) : (
              `${verbCap} ${count} Line${count > 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One line a bulk operation could not process. */
export interface BulkResultLogEntry {
  lineId: number;
  poNumber: string;
  lineNumber: number;
  error: string;
}

interface BulkResultLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: BulkResultLogEntry[];
}

export function BulkResultLogDialog({ open, onOpenChange, entries }: BulkResultLogDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Bulk Operation — Failed Lines</DialogTitle>
          <DialogDescription>
            {entries.length} line{entries.length !== 1 ? 's' : ''} could not be processed. Failed lines remain selected in the table.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b">
                <th className="text-left px-3 py-2 font-medium">PO Number</th>
                <th className="text-left px-3 py-2 font-medium">Line</th>
                <th className="text-left px-3 py-2 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.lineId} className="border-b last:border-0">
                  <td className="px-3 py-2">{entry.poNumber}</td>
                  <td className="px-3 py-2">{entry.lineNumber}</td>
                  <td className="px-3 py-2 text-red-600">{entry.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
